import React, { useState } from 'react';
import { ArrowLeft, Send, Mail, User, MessageSquare, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';

interface ContactFormProps {
  onBack: () => void;
}

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export function ContactForm({ onBack }: ContactFormProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // EmailJS Integration with proper parameter mapping
      await emailjs.send('service_xhf60or', 'template_kvkvw2h', {
        // Standard parameters that EmailJS templates commonly use
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        
        // Alternative parameter names for compatibility
        from_name: formData.name,
        from_email: formData.email,
        user_name: formData.name,
        user_email: formData.email,
        reply_to: formData.email,
        
        // Destination email
        to_email: 'corp.one@cosmicforces.com',
        to_name: 'Cosmic Forces Team',
        
        // Additional metadata
        timestamp: new Date().toLocaleString(),
        form_type: 'Contact Form'
      }, 'rgXF0AiMHaekkrzWA');
      
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
      
      // Reset form after a delay
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setIsSubmitted(false);
      }, 4000);
      
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name.trim() && 
                     formData.email.trim() && 
                     formData.subject.trim() && 
                     formData.message.trim();

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="border-b border-cyan-500/20 bg-slate-900/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors duration-300"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="font-cyberpunk">Back to Home</span>
            </button>
            
            <div className="text-2xl bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent font-cyberpunk">
              COSMIC FORCES
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-full bg-gradient-to-r from-cyan-500/20 to-purple-600/20 border border-cyan-500/30">
                <Mail className="h-12 w-12 text-cyan-400" />
              </div>
            </div>
            
            <h1 className="text-4xl mb-4 bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent font-cyberpunk">
              CONTACT US
            </h1>
            
            <p className="text-gray-400 text-lg max-w-md mx-auto">
              Get in touch with the Cosmic Forces team. We'd love to hear from you.
            </p>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20 p-8 backdrop-blur-sm">
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="flex justify-center mb-6">
                  <div className="p-4 rounded-full bg-gradient-to-r from-green-500/20 to-cyan-500/20 border border-green-500/30">
                    <Check className="h-12 w-12 text-green-400" />
                  </div>
                </div>
                <h2 className="text-2xl mb-4 text-green-400 font-cyberpunk">
                  MESSAGE SENT!
                </h2>
                <p className="text-gray-400 mb-6">
                  Thank you for your message. We'll get back to you within 24-48 hours.
                </p>
                <div className="text-sm text-gray-500">
                  Form will reset automatically...
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Field */}
                <div className="space-y-2">
                  <label htmlFor="name" className="flex items-center space-x-2 text-cyan-400 font-cyberpunk">
                    <User className="h-4 w-4" />
                    <span>Name</span>
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your full name"
                    required
                    className="bg-slate-700/50 border-cyan-500/30 focus:border-cyan-400 text-white placeholder-gray-400"
                  />
                </div>

                {/* Email Field */}
                <div className="space-y-2">
                  <label htmlFor="email" className="flex items-center space-x-2 text-cyan-400 font-cyberpunk">
                    <Mail className="h-4 w-4" />
                    <span>Email</span>
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                    required
                    className="bg-slate-700/50 border-cyan-500/30 focus:border-cyan-400 text-white placeholder-gray-400"
                  />
                </div>

                {/* Subject Field */}
                <div className="space-y-2">
                  <label htmlFor="subject" className="flex items-center space-x-2 text-cyan-400 font-cyberpunk">
                    <MessageSquare className="h-4 w-4" />
                    <span>Subject</span>
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="What's this about?"
                    required
                    className="bg-slate-700/50 border-cyan-500/30 focus:border-cyan-400 text-white placeholder-gray-400"
                  />
                </div>

                {/* Message Field */}
                <div className="space-y-2">
                  <label htmlFor="message" className="flex items-center space-x-2 text-cyan-400 font-cyberpunk">
                    <MessageSquare className="h-4 w-4" />
                    <span>Message</span>
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us what's on your mind..."
                    required
                    rows={6}
                    className="bg-slate-700/50 border-cyan-500/30 focus:border-cyan-400 text-white placeholder-gray-400 resize-none"
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={!isFormValid || isSubmitting}
                    className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white font-cyberpunk disabled:opacity-50 disabled:cursor-not-allowed group"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Sending...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 group-hover:scale-105 transition-transform duration-200">
                        <Send className="h-4 w-4" />
                        <span>Send Message</span>
                      </div>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="mt-8 p-4 bg-slate-800/30 rounded-lg border border-cyan-500/20">
            <div className="text-sm">
              <h3 className="text-cyan-400 font-cyberpunk mb-2">📧 Email Response</h3>
              <p className="text-gray-400">
                We typically respond within 24-48 hours during business days.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}